import { format, parseISO } from 'date-fns';
import type { TFunction } from 'i18next';
import { DateRange } from 'react-day-picker';

export const DEFAULT_CHAT_AGENT_ID = '000000000000000000000000';

export function formatNumber(value: number) {
  return value.toLocaleString();
}

/**
 * Parses the calendar-date portion of an ISO value without applying a timezone
 * offset. The usage API returns `period.end` as an end-of-day UTC timestamp
 * (e.g. `2026-09-10T23:59:59Z`); parsing that directly shifts the day forward in
 * timezones ahead of UTC, which made the range read one day into the future.
 */
export function parseDateOnly(value: string) {
  return parseISO(value.slice(0, 10));
}

export function formatShortDate(value: string) {
  return format(parseDateOnly(value), 'MMM dd');
}

export function formatWindow(start: string, end: string) {
  return `${format(parseDateOnly(start), 'MMM dd')} - ${format(parseDateOnly(end), 'MMM dd, yyyy')}`;
}

export function formatRangeLabel(range: DateRange | undefined, selectRangeLabel: string) {
  if (!range?.from) return selectRangeLabel;
  if (!range.to) return format(range.from, 'MMM dd, yyyy');
  return `${format(range.from, 'MMM dd')} - ${format(range.to, 'MMM dd, yyyy')}`;
}

export function getRatio(prompt: number, completion: number) {
  if (!prompt) return '—';
  return `${(completion / prompt).toFixed(3)}x`;
}

export function getAverage(total: number, count: number) {
  if (!count) return '—';
  return formatNumber(Math.round(total / count));
}

export function getUserLabel(userId: string | null | undefined) {
  if (!userId) return 'unknown';
  if (userId.length <= 16) return userId;
  return `${userId.slice(0, 8)}...${userId.slice(-8)}`;
}

/** Admins name limit windows themselves; only the stock "Daily limit" name follows the viewer's language. */
export function formatWindowName(name: string, t: TFunction) {
  if (name.trim().toLowerCase() === 'daily limit') {
    return t('DAILY_LIMIT', { defaultValue: 'Daily limit' });
  }
  return name;
}

/** "in 21 hr 19 min" within a day, else "tomorrow at 11:50 PM" or "Oct 3 at 11:50 PM". Empty once past. */
export function formatResetTime(iso: string, t: TFunction, now = new Date()) {
  const at = new Date(iso);
  const msLeft = at.getTime() - now.getTime();
  if (Number.isNaN(msLeft) || msLeft <= 0) return '';

  if (msLeft < 24 * 60 * 60 * 1000) {
    const totalMinutes = Math.ceil(msLeft / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0) return t('RESETS_IN_MINUTES', { minutes, defaultValue: 'in {{minutes}} min' });
    return t('RESETS_IN_HOURS_MINUTES', { hours, minutes, defaultValue: 'in {{hours}} hr {{minutes}} min' });
  }

  const time = at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  if (at.toDateString() === tomorrow.toDateString()) {
    return t('RESETS_TOMORROW_AT', { time, defaultValue: 'tomorrow at {{time}}' });
  }
  const date = at.toLocaleDateString([], { month: 'short', day: 'numeric' });
  return t('RESETS_ON_DATE_AT', { date, time, defaultValue: '{{date}} at {{time}}' });
}

/**
 * Whole when there is no fraction, else up to `decimals` places so a little use still shows: 0.4%.
 * Any use under the smallest place reads "<0.1%". Limits pass 2, so 33.33% is not cut to 33.3%.
 */
export function formatPercent(value: number, decimals = 1) {
  if (!Number.isFinite(value) || value <= 0) return '0%';
  const smallest = 10 ** -decimals;
  if (value < smallest) return `<${smallest}%`;
  return `${Number(Math.min(100, value).toFixed(decimals))}%`;
}
